import React, { useMemo } from 'react';
import {
  Typography,
  Spacing,
  StyledDiv,
  ImageContainer,
} from '@taleemabad/dsm/components';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { getAlphabetFromIndex } from '@taleemabad/dsm/utils';
import { MathJaxComponent } from '../math-jax/math-jax';
import { QuestionSubType } from '@taleemabad/db';

export interface McqsOptionsProps {
  answerOptions: AnswerOption[];
  isUrdu: boolean;
  showAnswer?: boolean;
  subType?: string;
}

interface AnswerOption {
  statement: string;
  media: Media[];
  isCorrect: boolean;
}

interface Media {
  url: string;
}

const renderOptionStatement = (statement: string, subType?: string) => {
  const hasFractions = /\[(\d{1,5})\/(\d{1,5})\]/g.test(statement);
  if (
    hasFractions ||
    subType === QuestionSubType.VerticalText ||
    subType === QuestionSubType.VerticalTextWithUnits
  ) {
    return (
      <Typography variant="textBodySmallOnSurfaceVariant">
        <MathJaxComponent
          question={statement}
          questionType={subType as QuestionSubType}
        />
      </Typography>
    );
  }
  return (
    <Typography variant="textBodySmallOnSurfaceVariant">{statement}</Typography>
  );
};

const renderOptionMedia = (media: Media[]) => {
  return media.map(
    (mediaItem) =>
      mediaItem.url && (
        <ImageContainer
          key={mediaItem.url}
          src={mediaItem.url}
          alt="question"
        />
      ),
  );
};

export const McqsOptions: React.FC<McqsOptionsProps> = ({
  answerOptions,
  isUrdu,
  showAnswer,
  subType,
}) => {
  const renderedOptions = useMemo(
    () =>
      answerOptions.map((option, index) => (
        <Option key={option.statement + index} isUrdu={isUrdu}>
          <TextContainer isSelected={showAnswer && option.isCorrect}>
            <StyledDiv alignItem="center">
              <Typography variant="textBodySmallOnSurfaceVariant">
                {getAlphabetFromIndex(index, isUrdu)}:
              </Typography>
              <Spacing margin="none md">
                {renderOptionStatement(option.statement, subType)}
                {renderOptionMedia(option.media)}
              </Spacing>
            </StyledDiv>
          </TextContainer>
        </Option>
      )),
    [answerOptions, showAnswer, subType, isUrdu],
  );

  return (
    <OptionsContainer isUrdu={isUrdu} dir={isUrdu ? 'rtl' : 'ltr'}>
      {renderedOptions}
    </OptionsContainer>
  );
};

const OptionsContainer = styled.div<{ isUrdu: boolean }>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--rk-spacing-xs);
  margin: var(--rk-spacing-sm) 0 0 var(--rk-spacing-xl);
  direction: ${({ isUrdu }) => (isUrdu ? 'rtl' : 'ltr')};
  align-items: flex-start;
  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Option = styled.div<{ isUrdu: boolean }>`
  display: flex;
  flex-direction: ${({ isUrdu }) => (isUrdu ? 'row-reverse' : 'row')};
  align-items: center;
  margin: var(--rk-spacing-sm) 0;

  img {
    max-width: 100px;
    margin-inline-start: var(--rk-spacing-md);
  }
`;

const TextContainer = styled.div<{ isSelected?: boolean }>`
  background-color: ${({ isSelected }) =>
    isSelected ? 'var(--md-ref-secondary-70)' : 'transparent'};
  border-radius: 12px;
  padding: 0 var(--rk-spacing-md);
`;
